import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { vmiApi, type VmiDeclarationDto, type VmiDeclarationSummaryDto } from '../../api/vmiDeclarations';
import { useReportScreen } from '../../components/ScreenContext';
import LoadingSpinner from '../../components/LoadingSpinner';
import MoneyCell from '../../components/MoneyCell';

const STATUS_CHIP: Record<string, string> = {
  Draft: 'bg-amber-50 text-amber-700 border-amber-200',
  UnderReview: 'bg-blue-50 text-blue-700 border-blue-200',
  Approved: 'bg-green-50 text-green-700 border-green-200',
  Locked: 'bg-primary-50 text-primary-700 border-primary-200',
};

const GPM313_BOXES = [
  { key: 'G5', label: 'G5 — Darbo pajamos išmokėtos' },
  { key: 'G6', label: 'G6 — GPM iki 15 d.' },
  { key: 'G7', label: 'G7 — GPM po 15 d.' },
  { key: 'G8', label: 'G8 — Kitos pajamos' },
  { key: 'G9', label: 'G9 — Kitų GPM iki 15 d.' },
  { key: 'G10', label: 'G10 — Kitų GPM po 15 d.' },
  { key: 'G11', label: 'G11 — B klasės pajamos' },
  { key: 'G12', label: 'G12 — B klasės mokestis' },
];

/**
 * D3–D5 — VMI deklaracijos (GPM313 mėnesinė; GPM312 metinė ir FR0471 ateis
 * per tą patį lapą): generavimas → peržiūra (GPM313 dėžės) → išsaugojimas →
 * submit/approve/lock darbo eiga (dviejų asmenų taisyklė) → CSV.
 */
export default function VmiDeclarations() {
  const queryClient = useQueryClient();
  const now = new Date();
  const [year, setYear] = useState(now.getFullYear());
  const [vmiType, setVmiType] = useState<'GPM313' | 'GPM312' | 'FR0471'>('GPM313');
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [from, setFrom] = useState(`${now.getFullYear()}-01-01`);
  const [to, setTo] = useState(`${now.getFullYear()}-12-31`);
  const [preview, setPreview] = useState<VmiDeclarationDto | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [actor, setActor] = useState('');

  const { data: saved } = useQuery({
    queryKey: ['vmi-declarations'],
    queryFn: vmiApi.list,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['vmi-declarations'] });

  const run = async (fn: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await fn();
      await refresh();
    } catch (e: any) {
      setError(e.message || 'Nepavyko');
    } finally {
      setBusy(false);
    }
  };

  useReportScreen(
    `VMI deklaracijos: ${saved?.length ?? 0} išsaugota${preview ? ` (peržiūra GPM313 ${year}-${month})` : ''}`,
    { recordCount: saved?.length ?? 0, filters: { metai: String(year), menuo: String(month) } },
  );

  const boxes = vmiType === 'GPM313' ? (preview?.rows[0]?.extras ?? null) : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between py-2 border-b border-gray-100">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">VMI deklaracijos</h1>
          <p className="text-sm text-gray-500">
            GPM313 mėnesinė — pajamos pagal išmokėjimo mėnesį (iki/po 15 d.); GPM312 metinė (L/U priedai); FR0471 — B klasės išmokos.
          </p>
        </div>
        <Link
          to="/payroll"
          className="px-4 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:border-gray-400"
        >
          ← Darbo užmokestis
        </Link>
      </div>

      <div className="filters" style={{ display: 'flex', gap: '1rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <label>
          Tipas:{' '}
          <select value={vmiType} onChange={e => setVmiType(e.target.value as 'GPM313' | 'GPM312' | 'FR0471')}>
            <option value="GPM313">GPM313 (mėnesinė)</option>
            <option value="GPM312">GPM312 (metinė)</option>
            <option value="FR0471">FR0471 (B klasė)</option>
          </select>
        </label>
        {vmiType === 'FR0471' ? (
          <>
            <label>
              Nuo:{' '}
              <input type="date" value={from} onChange={e => setFrom(e.target.value)} />
            </label>
            <label>
              Iki:{' '}
              <input type="date" value={to} onChange={e => setTo(e.target.value)} />
            </label>
          </>
        ) : (
          <label>
            Metai:{' '}
            <input type="number" value={year} min={2000} max={2100} onChange={e => setYear(Number(e.target.value))} />
          </label>
        )}
        {vmiType === 'GPM313' && (
        <label>
          Mėnuo:{' '}
          <select value={month} onChange={e => setMonth(Number(e.target.value))}>
            {Array.from({ length: 12 }, (_, i) => i + 1).map(m => (
              <option key={m} value={m}>{m}</option>
            ))}
          </select>
        </label>
        )}
        <button onClick={async () => {
          setBusy(true); setError('');
          try {
            setPreview(
              vmiType === 'GPM313' ? await vmiApi.generate('GPM313', year, month)
              : vmiType === 'GPM312' ? await vmiApi.generate312(year)
              : await vmiApi.generate0471(from, to));
          }
          catch (e: any) { setError(e.message || 'Nepavyko sugeneruoti'); }
          finally { setBusy(false); }
        }} disabled={busy}>
          {busy ? <LoadingSpinner /> : `Generuoti ${vmiType}`}
        </button>
        {preview && (
          <button className="secondary" onClick={() => run(() =>
            vmiType === 'GPM313' ? vmiApi.save('GPM313', year, month)
            : vmiType === 'GPM312' ? vmiApi.save312(year)
            : vmiApi.save0471(from, to))} disabled={busy}>
            Išsaugoti juodraštį
          </button>
        )}
      </div>

      {error && <div className="error-message">{error}</div>}

      {preview && vmiType !== 'GPM313' && (
        <div className='card'>
          <h2 className='text-sm font-semibold text-gray-700 mb-2'>
            {vmiType} peržiūra — {vmiType === 'GPM312' ? year : `${from}…${to}`} ({preview.rows.length} eilutės)
          </h2>
          <table className='data-table'>
            <thead>
              <tr>
                <th>Nr.</th>
                <th>Asmens kodas</th>
                <th>Vardas pavardė</th>
                {vmiType === 'GPM312' && <th>Priedas</th>}
                <th>{vmiType === 'GPM312' ? 'Pajamų kodas' : 'Mokesčio kodas'}</th>
                <th style={{ textAlign: 'right' }}>{vmiType === 'GPM312' ? 'Pajamos' : 'Suma'}</th>
                <th style={{ textAlign: 'right' }}>{vmiType === 'GPM312' ? 'GPM' : 'Mokestis'}</th>
              </tr>
            </thead>
            <tbody>
              {preview.rows.map(r => (
                <tr key={r.rowNumber}>
                  <td>{r.rowNumber}</td>
                  <td>{r.personCode}</td>
                  <td>{r.personName}</td>
                  {vmiType === 'GPM312' && <td>{r.extras?.appendix}</td>}
                  <td>{r.extras?.incomeCode}</td>
                  <td style={{ textAlign: 'right' }}>{r.income.toFixed(2)}</td>
                  <td style={{ textAlign: 'right' }}>{r.tax.toFixed(2)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className='total-row'>
                <td colSpan={vmiType === 'GPM312' ? 5 : 4}><strong>Viso</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{preview.totalIncome.toFixed(2)}</strong></td>
                <td style={{ textAlign: 'right' }}><strong>{preview.totalTax.toFixed(2)}</strong></td>
              </tr>
            </tfoot>
          </table>
          {preview.warnings.map((w, i) => <p key={i} className='text-xs text-amber-700 mt-1'>⚠ {w}</p>)}
        </div>
      )}

      {preview && boxes && (
        <div className="card">
          <h2 className="text-sm font-semibold text-gray-700 mb-2">
            GPM313 peržiūra — {year}-{String(month).padStart(2, '0')}
          </h2>
          <table className="data-table">
            <thead>
              <tr>
                <th>Dėžė</th>
                <th style={{ textAlign: 'right' }}>Suma</th>
              </tr>
            </thead>
            <tbody>
              {GPM313_BOXES.map(b => (
                <tr key={b.key}>
                  <td>{b.label}</td>
                  <td style={{ textAlign: 'right' }}>{boxes[b.key] ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {preview.warnings.length > 0 && (
            <div className="mt-2 text-xs text-amber-700">
              {preview.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
            </div>
          )}
        </div>
      )}

      {saved && saved.length === 0 && (
        <div className="bg-white border border-gray-200 rounded-lg p-6 text-center text-sm text-gray-500">
          Išsaugotų deklaracijų kol kas nėra — sugeneruokite peržiūrą ir išsaugokite juodraštį.
        </div>
      )}

      {saved && saved.length > 0 && (
        <div className="bg-white border border-gray-200 rounded-lg overflow-x-auto">
          <table className="data-table">
            <thead>
              <tr>
                <th>Tipas</th>
                <th>Laikotarpis</th>
                <th>Būsena</th>
                <th style={{ textAlign: 'right' }}>Asmenys</th>
                <th style={{ textAlign: 'right' }}>Pajamos</th>
                <th style={{ textAlign: 'right' }}>Mokestis</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {saved.map((d: VmiDeclarationSummaryDto) => (
                <tr key={d.id}>
                  <td>{d.declarationType}</td>
                  <td>{d.year ?? (d.dateFrom?.slice(0, 10) + '…' + d.dateTo?.slice(0, 10))}</td>
                  <td>
                    <span className={`px-1.5 py-0.5 text-[10px] font-semibold rounded-full border ${STATUS_CHIP[d.status] ?? ''}`}>
                      {d.status}
                    </span>
                  </td>
                  <td style={{ textAlign: 'right' }}>{d.personCount}</td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={d.totalIncome} /></td>
                  <td style={{ textAlign: 'right' }}><MoneyCell value={d.totalTax} /></td>
                  <td>
                    <span className="flex gap-1">
                      {d.status === 'Draft' && (
                        <>
                          <input
                            style={{ width: '6rem' }}
                            placeholder="vardas"
                            value={actor}
                            onChange={e => setActor(e.target.value)}
                          />
                          <button onClick={() => run(() => vmiApi.action(d.id, 'submit', actor || 'user'))}>Pateikti</button>
                        </>
                      )}
                      {d.status === 'UnderReview' && (
                        <>
                          <button onClick={() => run(() => vmiApi.action(d.id, 'approve', actor || 'approver'))}>Patvirtinti</button>
                          <button onClick={() => run(() => vmiApi.action(d.id, 'reject', actor || 'user'))}>Atmesti</button>
                        </>
                      )}
                      {d.status === 'Approved' && (
                        <button onClick={() => run(() => vmiApi.action(d.id, 'lock', actor || 'locker'))}>Užrakinti</button>
                      )}
                      <button className="secondary" onClick={() => vmiApi.exportCsv(d.id)}>CSV</button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
